import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CheckboxInput, CommonInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { postTradeInLeadCreation } from 'store/TradeInState';
import { RootState } from 'store/store';
import VehicleDetails from '../sub-component/VehicleDetails';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';

import Calendar from '../icon/calendar.svg';
import Time from '../icon/time.svg';
import Edit from '../icon/edit.svg';

const Step2: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();
  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const leadSourceId = authenticateClientData?.data?.widgetData?.leadSourceId;
  const zipCode = authenticateClientData.data?.widgetData?.zipCode;
  const dealershipName = authenticateClientData.data?.widgetData?.dealershipName;
  const { control, trigger, getValues, setValue } = useFormContext();

  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { firstName, lastName, phone, email } = getValues();

    const updatedData = {
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      email: email,
      leadSourceId: leadSourceId,
      country: 'CA',
      birthDate: '1991-01-01',
      zipCode: zipCode,
    };

    dispatch(
      postTradeInLeadCreation({
        data: updatedData,
        leadTypeID: authenticateClientData?.data?.widgetData?.leadTypeId,
      })
    ).then((res) => {
      if (Object.keys(res?.payload).length && res?.payload?.message) {
        onNext?.();
      }
    });
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <div className="car-details">
        <div className="car-details__heading">
          <p>{t('bookTestDrive.title')}</p>
        </div>
      </div>
      <VehicleDetails />
      <hr className="mb-4" />
      <div className="bookappt-dt">
        <div className="bookappt-dt__col">
          <div className="bookappt-dt__icon">
            <img src={Calendar} alt="Calendar" />
          </div>
          <div className="bookappt-dt__desc">
            <span className="lbl">Date</span>
            <div className="value">15th January 2025</div>
          </div>
        </div>
        <div className="bookappt-dt__col">
          <div className="bookappt-dt__icon">
            <img src={Time} alt="Time" />
          </div>
          <div className="bookappt-dt__desc">
            <span className="lbl">Time</span>
            <div className="value">02:00 pm</div>
          </div>
        </div>
        <div className="bookappt-dt__col">
          <button type="button" className="btn">
            <img src={Edit} alt="Edit" />
          </button>
        </div>
      </div>
      <div className="two-col">
        <div className="form-group">
          <CommonInput
            name='firstName'
            control={control}
            label={t('stepperForm.firstName')}
            placeholder={t('stepperForm.enterFirstName')}
            rules={formRequiredRules(errorMessage(t).firstNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className="form-group">
          <CommonInput
            name='lastName'
            control={control}
            label={t('stepperForm.lastName')}
            placeholder={t('stepperForm.enterLastName')}
            rules={formRequiredRules(errorMessage(t).lastNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className="form-group">
          <CommonPhoneInput
            name='phone'
            control={control}
            label={t('stepperForm.mobileNumber')}
            placeholder={t('stepperForm.enterNumber')}
            rules={{
              ...formRequiredRules(errorMessage(t).phoneRequired),
              ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
        <div className="form-group">
          <CommonInput
            name='email'
            control={control}
            hint={t('stepperForm.emailNote')}
            label={t('stepperForm.emailEg')}
            placeholder={t('stepperForm.enterEmail')}
            rules={{
              ...formRequiredRules(errorMessage(t).emailRequired),
              ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
      </div>
      <div className="form-group">
        <AutoCompleteAddress
          name='testDriveAddress'
          control={control}
          setValue={setValue}
          label={t('stepperForm.address')}
          placeholder={t('stepperForm.enterAddress')}
          rules={formRequiredRules(errorMessage(t).addressRequired)}
          triggerValidationForField={trigger}
        />
      </div>
      <div className="form-group">
        <CheckboxInput
          name='termsAndCondition'
          control={control}
          label={t('checkAvailability.checkBoxNote', { dealershipName })}
          rules={formRequiredRules(errorMessage(t).termsAndConditionRequired)}
          triggerValidationForField={trigger}
        />
      </div>
    </>
  );
};

const StepperForm2 = React.forwardRef(Step2);

export default StepperForm2;
